/* eslint-disable @typescript-eslint/naming-convention */
import { EntityDto, FullAuditedEntityDto, PagedAndSortedResultRequestDto } from '@abp/ng.core';
import { LanguageDto } from '@proxy/language/language.model';
import { IntegrationUsageStatus, ProjectDto } from 'src/app/projects/proxy/project.models';

export interface IntegrationDto extends EntityDto<string> {
  platform: string;
  provider: string;
  fields: string;
  name: string;
  uniqueId: string;
  integrationUsageStatus: IntegrationUsageStatus;
}

export interface ChannelIntegrationDto extends IntegrationDto {
  projectId?: string;
  projectName?: string;
}

export interface ServiceIntegrationDto extends IntegrationDto {
  languageId?: string;
  projects?: ProjectDto[];
  isReadonly?: boolean;
}

export interface GetIntegrationsInput extends PagedAndSortedResultRequestDto {
  filterText?: string;
  name?: string;
  type: number;
}

export interface IntegrationCreateUpdateDtoBase {
  id?: string;
  name: string;
  platform: string;
  provider: string;
  fields: string;
  uniqueId: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ChannelIntegrationCreateUpdateDto extends IntegrationCreateUpdateDtoBase {}

export interface ServiceIntegrationCreateUpdateDto extends IntegrationCreateUpdateDtoBase {
  languageId?: string;
}

export interface MultipleInputGroup {
  maxRowCount: number;
  inputs: IntegrationInput[];
}

export interface InputRule {
  type: RuleType;
  conditions: RuleCondition[];
  sourceInputNameForValue: string;
  value: string;
}

export interface RuleCondition {
  inputName: string;
  inputValue: string;
  operator: ConditionOperator;
}

export interface SelectOption {
  key: string;
  value: string;
  description?: string;
}

export interface IntegrationInput {
  name: string;
  isMasked: boolean;
  isMandatory: boolean;
  isEncrypted: boolean;
  isAppUniqueId: boolean;
  description: string;
  defaultValue: string;
  type: IntegrationInputType;
  range: number[];
  stringLength: number[];
  tooltip: string;
  placeholder: string;
  valueTemplate: string;
  validationPattern: string;
  tabGroup: string;
  cellGroup: number;
  selectOptions: SelectOption[];
  isReadOnly: boolean;
  isHidden: boolean; // added for UI only, not in the API
  multipleInputGroup: MultipleInputGroup;
  inputGroupInputs: IntegrationInput[];
  inputRules: InputRule[];
}

export interface RavenIntegrationDto {
  name: string;
  provider: string;
  platform: string;
  channelId: string;
  inputs: IntegrationInput[];
}

export interface RavenMetaDataListDto {
  version: string;
  integrations: RavenIntegrationDto[];
  platforms: string[];
}

export interface IntegrationAvailabilityDto {
  platform: string;
  provider: string;
  isAvailable: boolean;
}

export interface IntegrationSupportedLanguagesDto {
  platform: string;
  provider: string;
  languages: LanguageDto[];
}

export interface GetLlmConfigurationsInput extends PagedAndSortedResultRequestDto {
  filterText?: string;
}

export class LlmConfigurationSummaryDto extends FullAuditedEntityDto<string> {
  name: string;
  provider: string;
}

export class LlmConfigurationDto extends FullAuditedEntityDto<string> {
  name: string;
  provider: LlmConfigurationProvider;
  openAiApiKey?: string;
  azureOpenAiApiKey?: string;
  azureOpenAiApiBase?: string;
  azureOpenAiDeploymentName?: string;
  azureOpenAiApiVersion?: string;
  awsBedrockRegion?: string;
  awsBedrockAccessKeyId?: string;
  awsBedrockSecretAccessKey?: string;
  googleGeminiApiKey?: string;
  groqApiKey?: string;
  togetherAiApiKey?: string;
  vllmApiBase?: string;
  vllmApiKey?: string;
  vertexAiProjectId?: string;
  vertexAiLocation?: string;
  vertexAiPrivateKey?: string;
  vertexAiClientEmail?: string;
  vertexAiTokenUri?: string;
}

export interface LlmConfigurationProviderModelsDto {
  provider: LlmConfigurationProvider;
  models: LlmConfigurationModelDto[];
}

export interface LlmConfigurationModelDto {
  name: string;
  supportsReasoning?: boolean;
  isLegacy: boolean;
}

export enum LlmConfigurationProvider {
  openAi = 0,
  azureOpenAi = 1,
  awsBedrock = 2,
  googleGemini = 3,
  sestek = 4,
  groq = 5,
  togetherAi = 6,
  vertexAi = 7,
}

export interface GetMcpServersInput extends PagedAndSortedResultRequestDto {
  filterText?: string;
}

export class McpServerSummaryDto extends FullAuditedEntityDto<string> {
  name: string;
  url: string;
  connectionType: McpServerConnectionType;
}

export class McpServerDto extends FullAuditedEntityDto<string> {
  name: string;
  url: string;
  connectionType: McpServerConnectionType;
  isAuthenticationEnabled: boolean;
  authenticationHeaderName?: string;
  authenticationHeaderValue?: string;
}

export enum McpServerConnectionType {
  streamableHttp = 0,
  sse = 1,
}

export enum ReasoningEffort {
  default = 4,
  none = 0,
  low = 1,
  medium = 2,
  high = 3,
}

export enum IntegrationType {
  channel = 'channel',
  service = 'service',
}

export enum ServiceIntegrationPlatformType {
  SpeechRecognition = 'Speech Recognition',
  TextToSpeech = 'Text-to-Speech',
  Sentiment = 'Sentiment',
  VoiceBiometrics = 'Voice Biometrics',
  TextLanguageDetection = 'Text-Language Detection',
  LiveChat = 'Live Chat',
  Translation = 'Translation',
}

export enum ServiceIntegrationProviderType {
  Sestek = 'Sestek',
  Generic = 'Generic',
  Alotech = 'Alotech',
  Aacc = 'Aacc',
  Genesys = 'Genesys',
  Tegsoft = 'Tegsoft',
  Twilio = 'Twilio',
}

export enum ChannelIntegrationPlatformType {
  WebChat = 'WebChat',
}

export enum ChannelIntegrationProviderType {
  Sestek = 'Sestek',
}

export enum IntegrationInputType {
  text = 'text',
  number = 'number',
  bool = 'bool',
  select = 'select',
  color = 'color',
  dynamic = 'dynamic',
  inputGroup = 'inputGroup',
  multipleInputGroup = 'multipleInputGroup',
  chips = 'chips',
}

export enum RuleType {
  visibility,
  mandatory,
  setValue,
  setValueFromInput,
  resetValue,
  setReadOnly,
}

export enum ConditionOperator {
  equal,
  notEqual,
  greaterThan,
  greaterThanOrEqual,
  lessThan,
  lessThanOrEqual,
  contains,
  notContains,
  startsWith,
  endsWith,
}
